/**
 * Portfolios — who ends up with which customers, and what it costs them in hours.
 *
 * The change list is the point of this page, not the totals. A manager already
 * knows roughly how big each book is; what they cannot see anywhere else is what
 * a threshold or slot change would MOVE — which customers arrive, which leave,
 * and whether the two sides balance. So every row expands into
 * keeping / incoming / losing rather than just a count.
 *
 * Portfolios with `tier: null` are real and are shown. They exist when someone
 * holds pinned customers but staffs no tier — typically the manager. The
 * reference implementation wrote portfolios only for slot-holders, so those
 * customers fell out of every total while coverage still read complete; the
 * engine's deviation 3 exists to prevent exactly that, and hiding the row here
 * would put the bug back in the UI layer.
 *
 * SEARCH AND PIN ORDER are both here for one reason: each column is capped at 25
 * rows, and a book can run to several hundred. Pinning a customer used to move
 * it correctly and then hide it — the only feedback was two counters changing by
 * one. See `../../lib/search` for the ordering and matching rules.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Kpi,
} from '@andavo/web-ui';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  detailRowId,
  TableScroll,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { useStaffingData } from '../../StaffingDataProvider';
import { ScenarioBar } from '../../components/ScenarioBar';
import {
  isHypothetical,
  moveCsmToTier,
  moveHire,
  placeCsmInTier,
  departCsm,
  proposeHire,
  proposeMove,
  proposePlacement,
  reinstateCsm,
  withdrawHire,
} from '../../lib/scenario';
import { PinControl } from '../../components/PinControl';
import { csmKeyOf, rosterIdIndex } from '../../lib/model';
import { buildStaffingModel, StaffingModelError } from '../../lib/model';
import {
  matchesAccount,
  normalizeNeedle,
  pinnedFirst,
  portfolioMatches,
  unplacedMatches,
} from '../../lib/search';
import { ftes, hours, volumeM } from '../../lib/format';
import { DiagnosticsPanel } from '../../components/DiagnosticsPanel';
import { StaffingScreenState } from '../../components/StaffingScreenState';
import type { Account } from '@andavo/staffing-engine';

const DATA_COLUMNS = 7;
/** How many unplaced matches to name before summarising the rest. */
const NAMED_UNPLACED = 8;
const NO_PINS: ReadonlyMap<string, string> = new Map();

export default function StaffingPortfoliosPage() {
  const {
    read,
    loading,
    error,
    reload,
    scenario,
    setScenario,
    resetScenario,
    scenarioChanges,
  } = useStaffingData();
  const { can } = useMe();
  const rows = useExpandedRows();
  const [query, setQuery] = useState('');

  const model = useMemo(() => {
    if (!read) return null;
    try {
      return {
        ok: true as const,
        value: buildStaffingModel(read, {
          tiers: scenario.tiers,
          balanceBy: scenario.balanceBy,
          slots: scenario.slots,
          pins: scenario.pins,
          hires: scenario.hires,
          departures: scenario.departures,
        }),
      };
    } catch (err) {
      return {
        ok: false as const,
        message:
          err instanceof StaffingModelError
            ? err.message
            : 'Could not compute the staffing model.',
      };
    }
  }, [read, scenario]);

  const state = (
    <StaffingScreenState
      configured={read?.config.configured ?? false}
      error={error}
      loading={loading}
      modelError={model && !model.ok ? model.message : null}
      onResetScenario={resetScenario}
      scenarioChanges={scenarioChanges}
    />
  );
  if (!model || !model.ok || !read) return state;

  const m = model.value;
  const canManage = can('manage:staffing');
  const csmIdOf = rosterIdIndex(read.roster.roster);
  const tierLabel = (key: string) =>
    read.config.config?.tiers.find((t) => t.key === key)?.label ?? key;

  // csmId → (customerId → when it was pinned). Per CSM, because "pinned" is
  // asymmetric: a customer pinned to someone ELSE shows a Pin action from this
  // row (which moves the pin), not an Unpin, and only the owning book reorders
  // to put it on top.
  // The SCENARIO's pins, not the stored ones — otherwise a proposed move would
  // change the computed book while the list beside it still showed the old
  // owner, which is the worst of both answers.
  const livePins = scenario.pins ?? read.pins.pins;
  const pinsByCsm = new Map<string, Map<string, string>>();
  for (const pin of livePins) {
    // A tier-only decision names nobody, so it belongs to no book's pin list.
    if (pin.csmId === null) continue;
    let book = pinsByCsm.get(pin.csmId);
    if (!book) {
      book = new Map<string, string>();
      pinsByCsm.set(pin.csmId, book);
    }
    book.set(pin.customerId, pin.createdAt);
  }

  // Who a customer can be moved TO, and which bands a CSM can be moved BETWEEN.
  const movableCsms = read.roster.roster
    .filter((r) => r.active)
    .map((r) => ({ id: r.id, name: r.displayName }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const configuredTiers = scenario.tiers ?? read.config.config?.tiers ?? [];
  const tierKeys = configuredTiers.map((t) => t.key);
  // customerId → the tier it has been placed in by hand, so a row can show it.
  const placedIn = new Map(
    livePins
      .filter((p) => p.tierKey !== null)
      .map((p) => [p.customerId, p.tierKey as string]),
  );

  const moveCustomer = (customerId: string, csmId: string) => {
    const csm = movableCsms.find((c) => c.id === csmId);
    if (!csm) return;
    setScenario((prev) => ({
      ...prev,
      pins: proposeMove(prev.pins ?? read.pins.pins, {
        customerId,
        csmId,
        csmDisplayName: csm.name,
        // Sorts above every stored pin, so the customer just moved leads the
        // column it lands in — the same rule the real pins follow.
        at: '9999-12-31T00:00:00.000Z',
      }),
    }));
  };

  // Placing a customer in a tier BY HAND — the other half of a pin. Scenario
  // only, like every other move here: making it real is a separate, explicit
  // write, and the point of asking "what if this were strategic" is to see the
  // answer first.
  const placeCustomer = (customerId: string, tierKey: string) => {
    setScenario((prev) => ({
      ...prev,
      pins: proposePlacement(prev.pins ?? read.pins.pins, {
        customerId,
        // '' is the "put it back" option — the spend band decides again.
        tierKey: tierKey === '' ? null : tierKey,
        at: '9999-12-31T00:00:00.000Z',
      }),
    }));
  };

  const moveCsm = (csmId: string, fromTierKey: string, toTierKey: string) => {
    setScenario((prev) => ({
      ...prev,
      slots: moveCsmToTier(
        prev.slots ?? read.slots.slots,
        csmId,
        fromTierKey,
        toTierKey,
      ),
    }));
  };

  const needle = normalizeNeedle(query);
  const searching = needle.length > 0;
  // Searched against the WHOLE book, not the rendered rows — see unplacedMatches.
  const matched = searching
    ? m.accounts.filter((a) => matchesAccount(a, needle))
    : [];
  const unplaced = searching ? unplacedMatches(m.result, matched) : [];

  const hires = scenario.hires ?? [];
  /** Tiers nothing lands in by size — membership there is by hand only. */
  const namedTiers = new Set(
    configuredTiers.filter((t) => t.selection === 'named').map((t) => t.key),
  );

  const addHire = (tierKey: string) =>
    setScenario((prev) => ({
      ...prev,
      hires: proposeHire(prev.hires ?? [], tierKey),
    }));
  const removeHire = (id: string) =>
    setScenario((prev) => ({
      ...prev,
      hires: withdrawHire(prev.hires ?? [], id),
    }));
  /**
   * Model somebody leaving.
   *
   * Their seats go rather than emptying, so the tier shrinks and their book
   * redistributes across whoever is left — which is the question being asked
   * when someone gives notice. A backfill is this plus "Add a hypothetical
   * hire", which reads as the two decisions it is.
   */
  const depart = (csmId: string) =>
    setScenario((prev) => ({
      ...prev,
      departures: departCsm(prev.departures ?? [], csmId),
    }));
  const reinstate = (csmId: string) =>
    setScenario((prev) => ({
      ...prev,
      departures: reinstateCsm(prev.departures ?? [], csmId),
    }));

  const rehire = (id: string, tierKey: string) =>
    setScenario((prev) => ({
      ...prev,
      hires: moveHire(prev.hires ?? [], id, tierKey),
    }));

  /** Give someone who staffs nothing a seat — the sibling of moving one. */
  const placeCsm = (
    csm: { id: string; displayName: string },
    tierKey: string,
  ) =>
    setScenario((prev) => ({
      ...prev,
      slots: placeCsmInTier(prev.slots ?? read.slots.slots, csm, tierKey),
    }));

  // Portfolios paired with their load, sorted heaviest-first: the question a
  // manager opens this page with is "who is over capacity", not "who is first
  // alphabetically".
  const rowsData = m.result.portfolios
    .map((portfolio, i) => ({ portfolio, load: m.loads[i]! }))
    .filter(
      ({ portfolio }) => !searching || portfolioMatches(portfolio, needle),
    )
    .sort((a, b) => b.load.monthlyHours - a.load.monthlyHours);

  /**
   * Roster members the engine produced NO portfolio for.
   *
   * THE TWO TABS HAVE TO AGREE. Team lists everyone on the roster; this page
   * listed only the people the engine had something to say about — a portfolio
   * exists for someone holding a seat or a pin, and nobody else. So a colleague
   * who staffed nothing was on one tab and absent from the other, which reads as
   * data missing rather than as a person with no seat. Team is where tier
   * assignment is decided, so this page shows the same list and says what each
   * person is holding.
   *
   * Hidden while SEARCHING: the search is over customers, and someone with no
   * customers cannot match one. Leaving them in would answer "no portfolio holds
   * it" with a list of empty books.
   */
  /**
   * Engine keys of people the roster has never heard of — including anyone
   * REMOVED from the team, since removal deactivates them and the roster read
   * returns active people only.
   *
   * Needed on the row, not just in the diagnostics panel, because of what the
   * engine does with them: deviation 3 gives a departing owner a `tier: null`
   * portfolio so their book is seen to leave rather than silently vanishing.
   * That is the SAME SHAPE a manager holding pins has, and the two are opposite
   * situations — one is a deliberate arrangement, the other is a person who is
   * gone. Until this, both rows said "No tier".
   */
  const offRoster = new Set(m.diagnostics.ownersNotOnRoster);

  /** What each proposed hire actually picks up — the whole point of adding one. */
  const hireLoad = new Map<string, number>();
  m.result.portfolios.forEach((p, i) => {
    if (isHypothetical(p.csm))
      hireLoad.set(p.csm, (hireLoad.get(p.csm) ?? 0) + m.loads[i]!.accounts);
  });

  const withPortfolio = new Set(m.result.portfolios.map((p) => p.csm));
  const unplacedMembers = searching
    ? []
    : read.roster.roster
        .filter((r) => !withPortfolio.has(csmKeyOf(r)))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const totalMoves = m.result.portfolios.reduce(
    (sum, p) => sum + p.incoming.length,
    0,
  );

  return (
    <div className="flex flex-col gap-6">
      <ScenarioBar changes={scenarioChanges} onReset={resetScenario} />

      {/* Departed people have no portfolio row — their seats are gone — so the
          way back has to live somewhere that still exists. It doubles as the
          only place the two invisible consequences are stated. */}
      {m.diagnostics.departedInScenario.length > 0 ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-content-warning bg-surface-midground px-3 py-2"
          data-testid="staffing-departures"
          role="status"
        >
          <span className="font-medium text-content-warning text-sm">
            Modelled as leaving
          </span>
          {m.diagnostics.departedInScenario.map((d) => (
            <div className="flex flex-wrap items-center gap-2" key={d.csmId}>
              <span className="text-content-primary text-sm">
                {d.displayName}
              </span>
              <span className="text-content-secondary text-sm">
                {d.seats === 0
                  ? 'staffed no tier'
                  : `${d.seats} ${d.seats === 1 ? 'seat' : 'seats'} removed, so ${d.seats === 1 ? 'that tier is' : 'those tiers are'} one person smaller`}
                {d.pinsReleased > 0
                  ? `; ${d.pinsReleased} pinned ${d.pinsReleased === 1 ? 'customer' : 'customers'} released back to the balancer`
                  : ''}
                .
              </span>
              <Button
                onClick={() => reinstate(d.csmId)}
                size="link"
                variant="link"
              >
                reinstate
              </Button>
            </div>
          ))}
          <span className="text-content-secondary text-xs">
            Nothing here is saved. To model a backfill rather than absorbing the
            book, add a hypothetical hire as well.
          </span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Portfolios" value={String(m.result.portfolios.length)} />
        <Kpi
          label="Customers placed"
          value={String(m.result.coverage.assigned)}
        />
        <Kpi label="Customers moving" value={String(totalMoves)} />
        <Kpi
          label="FTEs needed"
          value={ftes(m.tierLoads.reduce((s, t) => s + t.ftesNeeded, 0))}
        />
      </div>

      <DiagnosticsPanel
        diagnostics={m.diagnostics}
        displayName={m.displayName}
        tierLabel={tierLabel}
      />

      <Card>
        <CardHeader>
          <CardTitle>Portfolios</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="field-label" htmlFor="staffing-account-search">
              Find a customer
            </label>
            <input
              autoComplete="off"
              className="search-input"
              id="staffing-account-search"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Customer name…"
              type="search"
              value={query}
            />
            {searching ? (
              <SearchSummary
                matched={matched}
                needle={query.trim()}
                portfolios={rowsData.length}
                tierLabel={tierLabel}
                total={m.accounts.length}
                unplaced={unplaced}
              />
            ) : null}
          </div>

          {rowsData.length === 0 ? (
            <p
              className="text-content-secondary text-sm"
              data-testid="staffing-search-no-rows"
            >
              No portfolio holds a customer matching “{query.trim()}”.
            </p>
          ) : (
            <TableScroll testId="staffing-portfolios-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <DisclosureHeaderCell />
                    <th>CSM</th>
                    <th>Tier</th>
                    <th className="text-right">Customers</th>
                    <th className="text-right">Booking volume</th>
                    <th className="text-right">Hours / mo</th>
                    <th className="text-right">FTEs</th>
                    <th className="text-right">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsData.map(({ portfolio, load }, index) => {
                    const key = `${portfolio.csm}::${portfolio.tier ?? 'none'}`;
                    const id = detailRowId('staffing-portfolio', key);
                    // A search expands what it matched. Leaving the rows closed
                    // would answer "some book holds it" and stop there, which is
                    // the half of the question nobody needed answered.
                    const open = searching || rows.isExpanded(key);
                    const zebra = zebraFor(index);
                    const label = m.displayName(portfolio.csm);
                    // A HYPOTHETICAL hire has no roster row and therefore no
                    // `csmId`, which is exactly right: `csmId` is the key every
                    // WRITE on this row travels on, so an invented person cannot
                    // be pinned to, moved to, or saved by construction rather
                    // than by a check somebody has to remember.
                    const hypothetical = isHypothetical(portfolio.csm);
                    const csmId = csmIdOf.get(portfolio.csm);
                    const pinnedHere =
                      (csmId === undefined
                        ? undefined
                        : pinsByCsm.get(csmId)) ?? NO_PINS;
                    const outgoing =
                      portfolio.losing.length +
                      Object.values(portfolio.otherOut).reduce(
                        (s, l) => s + l.length,
                        0,
                      );
                    return (
                      // The fragment carries the key: the row and its detail row are
                      // one logical record, and keying the children instead makes React
                      // re-key the pair on every expand.
                      <Fragment key={key}>
                        <tr data-zebra={zebra}>
                          <DisclosureCell
                            controls={id}
                            label={label}
                            onToggle={() => rows.toggle(key)}
                            open={open}
                          />
                          <td className="font-medium">
                            {label}
                            {hypothetical ? (
                              <>
                                {' '}
                                <Badge
                                  title="Nobody. A hypothetical hire, to see which tier one would relieve most. Nothing about them is saved."
                                  variant="warning"
                                >
                                  proposed
                                </Badge>{' '}
                                <Button
                                  onClick={() => removeHire(portfolio.csm)}
                                  size="link"
                                  variant="link"
                                >
                                  withdraw
                                </Button>
                              </>
                            ) : canManage && csmId !== undefined ? (
                              <>
                                {' '}
                                {/* The question asked the day somebody gives
                                    notice. A SCENARIO, not the Team tab's
                                    removal, which is a write and answers
                                    "they have left" rather than "what if". */}
                                <Button
                                  onClick={() => depart(csmId)}
                                  size="link"
                                  title={`Model ${label} leaving: their seats are removed and their book redistributes across whoever is left. Nothing is saved.`}
                                  variant="link"
                                >
                                  model as leaving
                                </Button>
                              </>
                            ) : null}
                          </td>
                          <td>
                            {hypothetical && canManage ? (
                              // WHICH tier the hire goes in is the entire
                              // question, so it is changed from the row rather
                              // than by withdrawing and re-adding.
                              <select
                                aria-label={`Tier for ${label}`}
                                className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                                onChange={(e) =>
                                  rehire(portfolio.csm, e.target.value)
                                }
                                value={portfolio.tier ?? ''}
                              >
                                {tierKeys.map((key) => (
                                  <option key={key} value={key}>
                                    {tierLabel(key)}
                                  </option>
                                ))}
                              </select>
                            ) : portfolio.tier === null ? (
                              offRoster.has(portfolio.csm) ? (
                                <Badge
                                  title="Not on the staffing roster — removed from the team, or never added. They staff nothing and hold nothing; their customers are listed here only so the book they are losing is visible rather than silently redistributed."
                                  variant="warning"
                                >
                                  Not on the team
                                </Badge>
                              ) : (
                                <Badge
                                  title="Holds pinned customers but staffs no tier — typically the manager."
                                  variant="default"
                                >
                                  No tier
                                </Badge>
                              )
                            ) : canManage && csmId !== undefined ? (
                              // MOVING A CSM BETWEEN TIERS is a scenario, never
                              // a write. Slots belong to a config version, so
                              // making it real means publishing one — and the
                              // point of asking "what if she moved to Medium" is
                              // to see the answer BEFORE committing to it.
                              <select
                                aria-label={`Tier for ${label}`}
                                className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                                onChange={(e) =>
                                  moveCsm(
                                    csmId,
                                    portfolio.tier as string,
                                    e.target.value,
                                  )
                                }
                                value={portfolio.tier}
                              >
                                {tierKeys.map((key) => (
                                  <option key={key} value={key}>
                                    {tierLabel(key)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              tierLabel(portfolio.tier)
                            )}
                          </td>
                          <td className="text-right tabular-nums">
                            {load.accounts}
                          </td>
                          <td className="text-right tabular-nums">
                            {volumeM(load.bookingVolumeM)}
                          </td>
                          <td className="text-right tabular-nums">
                            {hours(load.monthlyHours)}
                          </td>
                          <td className="text-right tabular-nums">
                            {ftes(load.ftesNeeded)}
                          </td>
                          <td className="text-right tabular-nums">
                            {portfolio.incoming.length > 0 ? (
                              <span className="text-content-success">
                                +{portfolio.incoming.length}
                              </span>
                            ) : null}
                            {portfolio.incoming.length > 0 && outgoing > 0
                              ? ' / '
                              : null}
                            {outgoing > 0 ? (
                              <span className="text-content-danger">
                                −{outgoing}
                              </span>
                            ) : null}
                            {portfolio.incoming.length === 0 && outgoing === 0
                              ? '—'
                              : null}
                          </td>
                        </tr>
                        {open ? (
                          <DetailRow
                            colSpan={totalColumns(DATA_COLUMNS)}
                            id={id}
                            zebra={zebra}
                          >
                            <div className="detail-panel grid gap-4 md:grid-cols-3">
                              <AccountList
                                accounts={portfolio.incoming}
                                canManage={canManage}
                                csmDisplayName={label}
                                csmId={csmId}
                                csmOptions={movableCsms}
                                needle={needle}
                                onChanged={reload}
                                onMove={moveCustomer}
                                onPlace={placeCustomer}
                                pinnedHere={pinnedHere}
                                placedIn={placedIn}
                                tierOptions={configuredTiers}
                                title="Arriving"
                              />
                              <AccountList
                                accounts={[
                                  ...portfolio.losing,
                                  ...Object.values(portfolio.otherOut).flat(),
                                ]}
                                canManage={canManage}
                                csmDisplayName={label}
                                csmId={csmId}
                                csmOptions={movableCsms}
                                needle={needle}
                                onChanged={reload}
                                onMove={moveCustomer}
                                onPlace={placeCustomer}
                                pinnedHere={pinnedHere}
                                placedIn={placedIn}
                                tierOptions={configuredTiers}
                                title="Leaving"
                              />
                              <AccountList
                                accounts={[
                                  ...portfolio.keeping,
                                  ...portfolio.pinned,
                                ]}
                                canManage={canManage}
                                csmDisplayName={label}
                                csmId={csmId}
                                csmOptions={movableCsms}
                                needle={needle}
                                onChanged={reload}
                                onMove={moveCustomer}
                                onPlace={placeCustomer}
                                pinnedHere={pinnedHere}
                                placedIn={placedIn}
                                tierOptions={configuredTiers}
                                title="Staying"
                              />
                            </div>
                          </DetailRow>
                        ) : null}
                      </Fragment>
                    );
                  })}
                  {/* Everyone else on the roster. See unplacedMembers — the two
                      tabs list the same team, and a colleague with no seat is a
                      person with no seat rather than a missing row. */}
                  {unplacedMembers.map((member, i) => (
                    <tr
                      data-testid={`staffing-unplaced-${member.id}`}
                      data-zebra={zebraFor(rowsData.length + i)}
                      key={`unplaced-${member.id}`}
                    >
                      <td />
                      <td className="font-medium">{member.displayName}</td>
                      <td>
                        {canManage ? (
                          <select
                            aria-label={`Tier for ${member.displayName}`}
                            className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                            onChange={(e) =>
                              placeCsm(
                                {
                                  id: member.id,
                                  displayName: member.displayName,
                                },
                                e.target.value,
                              )
                            }
                            value=""
                          >
                            <option value="">Staffs nothing</option>
                            {tierKeys.map((key) => (
                              <option key={key} value={key}>
                                {tierLabel(key)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Badge
                            title="On the roster, holding no seat and no pinned customer."
                            variant="default"
                          >
                            Staffs nothing
                          </Badge>
                        )}
                      </td>
                      {/* Dashes, not zeroes. Nobody computed a book of nothing
                          for this person — there is no book at all — and a row
                          of 0.00 would read as a measured emptiness. */}
                      <td className="text-right text-content-secondary">—</td>
                      <td className="text-right text-content-secondary">—</td>
                      <td className="text-right text-content-secondary">—</td>
                      <td className="text-right text-content-secondary">—</td>
                      <td className="text-right text-content-secondary">—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}

          {canManage ? (
            <HireModeller
              hires={hires.map((h) => ({
                ...h,
                accounts: hireLoad.get(h.id) ?? 0,
                named: namedTiers.has(h.tierKey),
              }))}
              onAdd={addHire}
              onRemove={removeHire}
              tierKeys={tierKeys}
              tierLabel={tierLabel}
              // Defaults to the first tier that takes accounts BY SIZE. A
              // hand-picked tier takes nothing by size, so a hire dropped into
              // one receives an empty book and the feature reads as broken on
              // the very first click.
              defaultTierKey={
                tierKeys.find((k) => !namedTiers.has(k)) ?? tierKeys[0] ?? ''
              }
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * "What if we hired someone?" — the cheapest question this page can answer.
 *
 * A hire is modelled, never recorded: the person is invented, given a seat in
 * one tier, and the engine redistributes that tier's book as if they were there.
 * What comes back is the thing a staffing case is actually made of — how much
 * load comes OFF the people already in that tier, and how much stays. Trying the
 * same hire in three tiers takes three clicks, and the biggest drop is the
 * answer to where the next person belongs.
 *
 * NOTHING HERE IS SAVED. Like every other move on this page it lives in browser
 * state and dies on reload, which is what lets a manager try six versions
 * without leaving six half-considered rows at the head of an immutable audit
 * trail. Turning one into reality means hiring somebody and adding them on the
 * Team tab.
 */
function HireModeller({
  defaultTierKey,
  hires,
  onAdd,
  onRemove,
  tierKeys,
  tierLabel,
}: {
  defaultTierKey: string;
  hires: readonly {
    id: string;
    displayName: string;
    tierKey: string;
    /** How many customers they actually picked up. */
    accounts: number;
    /** Their tier takes nothing by size — membership there is by hand only. */
    named: boolean;
  }[];
  onAdd: (tierKey: string) => void;
  onRemove: (id: string) => void;
  tierKeys: readonly string[];
  tierLabel: (tierKey: string) => string;
}) {
  const [tierKey, setTierKey] = useState(defaultTierKey);

  if (tierKeys.length === 0) return null;

  // A hire who picks up NOTHING is the confusing case, and it has two entirely
  // different innocent causes. Left unexplained the row reads as a broken
  // feature; both reasons are real answers to "where would a hire help", and
  // "not here" is one of them.
  const empty = hires.filter((h) => h.accounts === 0);

  return (
    <div
      className="flex flex-col gap-2 border-border-subtle border-t pt-4"
      data-testid="staffing-hire-modeller"
    >
      <div className="flex flex-wrap items-center gap-2">
        <label className="font-medium text-sm" htmlFor="staffing-hire-tier">
          Model a new hire in
        </label>
        <select
          className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
          id="staffing-hire-tier"
          onChange={(e) => setTierKey(e.target.value)}
          value={tierKey}
        >
          {tierKeys.map((key) => (
            <option key={key} value={key}>
              {tierLabel(key)}
            </option>
          ))}
        </select>
        <Button onClick={() => onAdd(tierKey)} size="sm" variant="secondary">
          Add to the scenario
        </Button>
        {hires.length > 0 ? (
          <Button
            onClick={() => hires.forEach((h) => onRemove(h.id))}
            size="link"
            variant="link"
          >
            withdraw all {hires.length}
          </Button>
        ) : null}
      </div>
      <p className="text-content-secondary text-xs">
        They appear in the table above as a portfolio marked{' '}
        <strong>proposed</strong>, and every existing book in that tier changes
        by whatever moves to them — the − column is the relief a hire would
        actually buy. Try the same hire in each tier; the largest drop is where
        the next person belongs. Nothing here is saved.
      </p>
      {empty.map((hire) => (
        <p className="text-content-warning text-xs" key={hire.id}>
          <strong>{hire.displayName}</strong> picks up nothing in{' '}
          {tierLabel(hire.tierKey)}.{' '}
          {hire.named
            ? 'Nothing lands in a hand-picked tier by size — its customers are placed there one at a time — so a hire there only takes work once customers are placed with them.'
            : 'Every customer in that tier is already locked to the person who owns them today, so there is nothing loose to distribute. That is a real answer: a hire here would not relieve anyone.'}
        </p>
      ))}
    </div>
  );
}

/**
 * What the search actually found, including what it found OUTSIDE the table.
 *
 * The second sentence is the one that matters. A customer in an unstaffed tier
 * is in no portfolio at all, so narrowing the table to "portfolios holding a
 * match" would show nothing and imply the customer does not exist. Naming them
 * here keeps the page's answer to "where is this account" complete.
 */
function SearchSummary({
  matched,
  needle,
  portfolios,
  tierLabel,
  total,
  unplaced,
}: {
  matched: readonly Account[];
  needle: string;
  portfolios: number;
  tierLabel: (key: string) => string;
  total: number;
  unplaced: readonly { account: Account; tierKey: string | null }[];
}) {
  const named = unplaced.slice(0, NAMED_UNPLACED);
  return (
    <div
      className="flex flex-col gap-1 text-content-secondary text-xs"
      data-testid="staffing-search-summary"
    >
      <p>
        {matched.length === 0
          ? `No customer in the book matches “${needle}”.`
          : `${matched.length} of ${total} customers match “${needle}”, in ${portfolios} ${
              portfolios === 1 ? 'portfolio' : 'portfolios'
            }.`}
      </p>
      {unplaced.length > 0 ? (
        <p className="text-content-warning" role="status">
          {unplaced.length} {unplaced.length === 1 ? 'is' : 'are'} in no
          portfolio — their tier has no staffed slot, so they are excluded from
          every total above:{' '}
          {named
            .map(
              (u) =>
                `${u.account.name} (${
                  u.tierKey === null ? 'no tier' : tierLabel(u.tierKey)
                })`,
            )
            .join(', ')}
          {unplaced.length > named.length
            ? `, and ${unplaced.length - named.length} more`
            : ''}
          .
        </p>
      ) : null}
    </div>
  );
}

/**
 * One column of the change list.
 *
 * Capped at 25 with an explicit remainder line rather than silently truncated: a
 * list that stops at 25 and says nothing reads as a complete book of 25, and
 * "how many customers is this person actually getting" is the question the page
 * exists to answer.
 *
 * The cap is also why order is not cosmetic. Pins sort to the top (newest first)
 * because pinning is the one action here that WRITES, and a write whose only
 * visible effect is two counters moving by one is a write nobody trusts.
 */
function AccountList({
  accounts,
  canManage,
  csmDisplayName,
  csmId,
  csmOptions,
  needle,
  onChanged,
  onMove,
  onPlace,
  pinnedHere,
  placedIn,
  tierOptions,
  title,
}: {
  accounts: readonly Account[];
  canManage: boolean;
  csmDisplayName: string;
  csmId: string | undefined;
  /** Everyone a customer could be moved to, in a scenario. */
  csmOptions: readonly { id: string; name: string }[];
  /** Normalized search needle; empty means show everything. */
  needle: string;
  onChanged: () => void;
  onMove: (customerId: string, csmId: string) => void;
  onPlace: (customerId: string, tierKey: string) => void;
  /** customerId → pinned-at, for pins on THIS CSM only. */
  pinnedHere: ReadonlyMap<string, string>;
  /** customerId → the tier it was placed in by hand, if any. */
  placedIn: ReadonlyMap<string, string>;
  /** Every tier a customer could be placed in. */
  tierOptions: readonly { key: string; label: string }[];
  title: string;
}) {
  const filtered =
    needle.length > 0
      ? accounts.filter((a) => matchesAccount(a, needle))
      : accounts;
  const ordered = pinnedFirst(filtered, pinnedHere);
  const shown = ordered.slice(0, 25);
  return (
    <div className="flex flex-col gap-1">
      <h4 className="font-medium text-sm">
        {/* Both numbers when filtering: "2 of 44" keeps the column's real size
            visible, so a search never reads as the book having shrunk. */}
        {title} (
        {needle.length > 0
          ? `${filtered.length} of ${accounts.length}`
          : accounts.length}
        )
      </h4>
      {ordered.length === 0 ? (
        <p className="text-content-secondary text-xs">
          {needle.length > 0 && accounts.length > 0
            ? 'No match here.'
            : 'None.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {shown.map((a) => (
            <li
              className="flex items-center justify-between gap-2 text-content-secondary text-xs"
              key={a.id}
            >
              <span>
                {a.name}{' '}
                <span className="tabular-nums">({volumeM(a.spendM)})</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {canManage ? (
                  // WHAT IF THIS ONE MOVED. A select rather than a drag: the
                  // destination is one person out of twenty, and the row it
                  // would land in is usually off-screen. Choosing a name is the
                  // whole gesture.
                  //
                  // The value is always '' so it reads as an ACTION rather than
                  // as a field showing the current owner — the owner is the
                  // column this row is sitting in.
                  <select
                    aria-label={`Move ${a.name} to another CSM (scenario)`}
                    className="max-w-28 rounded border border-border-medium bg-surface-background px-1 py-0.5 text-xs"
                    onChange={(e) => {
                      if (e.target.value) onMove(a.id, e.target.value);
                    }}
                    value=""
                  >
                    <option value="">Move to…</option>
                    {csmOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                ) : null}
                {canManage ? (
                  // PLACE IT IN A TIER, by hand. Unlike "move to", this one
                  // SHOWS its current value rather than reading as an action:
                  // "which tier is this served at" is a fact about the customer
                  // that a reader needs, and a placement is invisible otherwise
                  // — it looks exactly like an account whose spend put it there.
                  <select
                    aria-label={`Tier for ${a.name} (scenario)`}
                    className="max-w-28 rounded border border-border-medium bg-surface-background px-1 py-0.5 text-xs"
                    onChange={(e) => onPlace(a.id, e.target.value)}
                    value={placedIn.get(a.id) ?? ''}
                  >
                    {/* Not "none" — putting it back means the spend band
                        decides again, which is a different thing from having no
                        tier at all. */}
                    <option value="">By size</option>
                    {tierOptions.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                ) : null}
                <PinControl
                  canManage={canManage}
                  csmDisplayName={csmDisplayName}
                  csmId={csmId}
                  customerId={a.id}
                  customerName={a.name}
                  onChanged={onChanged}
                  // Pinned HERE, specifically. A customer pinned to someone
                  // else shows a Pin action (which moves the pin), not an Unpin.
                  pinned={pinnedHere.has(a.id)}
                />
              </span>
            </li>
          ))}
          {ordered.length > shown.length ? (
            <li className="text-content-secondary text-xs italic">
              and {ordered.length - shown.length} more
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
